'use client';

import { parseAsInteger, parseAsString, useQueryState, useQueryStates } from 'nuqs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { childSearch, getChild, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import type { ChildAvailabilityItem, ChildAvailabilitySearchResults, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import type { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { filteredCountries } from './utils/filtered-countries.ts';
import { getDisplayIndex } from './utils/get-display-index.ts';
const LAST_STEP = 4;
const SECOND_TO_LAST_STEP = 3;
const DEFAULT_TAKE = 120;
export type ResultsStatus = 'idle' | 'loading' | 'matched' | 'fallback' | 'error';
export const useHelpMeChoose = () => {
  const [step, setStep] = useQueryState('step', parseAsInteger.withDefault(0));
  const [causes, setCauses] = useQueryState('filters', parseAsString);
  const [gender, setGender] = useQueryState('gender', parseAsString);
  const [ages, setAges] = useQueryStates({
    ageMin: parseAsInteger,
    ageMax: parseAsInteger
  });
  const [location, setLocation] = useQueryState('countries', parseAsString);
  const [children, setChildren] = useState<ChildAvailabilityItem[]>([]);
  const [childDetails, setChildDetails] = useState<ChildDetails>();
  const [skipChild, setSkipChild] = useState<number>(0);
  const [resultsStatus, setResultsStatus] = useState<ResultsStatus>('idle');
  const fetchIdRef = useRef(0);
  const lastFetchedKeyRef = useRef<string | null>(null);
  const filterKey = `${causes}|${gender}|${ages.ageMin}|${ages.ageMax}|${location}`;
  const isSkipped = (() => {
    switch (Number(step)) {
      case 0:
        return !causes;
      case 1:
        return !gender;
      case 2:
        return !(ages.ageMin || ages.ageMax);
      case 3:
        return !location;
      default:
        return false;
    }
  })();
  const hasCriteria = useCallback((): boolean => {
    return Boolean(gender || ages.ageMin || ages.ageMax || causes && causes !== 'anywhere' || filteredCountries(causes, location));
  }, [gender, ages.ageMin, ages.ageMax, causes, location]);
  const replaceDetails = async (child: ChildAvailabilityItem) => {
    try {
      const details = await getChild(child?.globalId);
      setChildDetails(details);
    } catch {
      console.error('Child missing details');
      setChildDetails(undefined);
    }
  };
  const fetchChildren = useCallback(async () => {
    fetchIdRef.current += 1;
    const id = fetchIdRef.current;
    setResultsStatus('loading');
    const baseSearchParams = {
      take: DEFAULT_TAKE,
      gender: gender as ChildGenderEnum,
      ageMin: ages.ageMin || 1,
      ageMax: ages.ageMax || 22,
      skip: 0,
      countries: filteredCountries(null, location) ? filteredCountries(null, location)!.split(',') : []
    };
    let childResults: ChildAvailabilitySearchResults | undefined;
    let details_0: ChildDetails | undefined;
    let status: ResultsStatus = 'matched';
    try {
      if (hasCriteria()) {
        childResults = await childSearch({
          ...baseSearchParams,
          disability: causes === 'hasDisabilities' ? true : undefined,
          isInHivAffectedArea: causes === 'affectedHiv' ? true : undefined,
          countries: filteredCountries(causes, location) ? filteredCountries(causes, location)?.split(',') : []
        });
      }
      if (hasCriteria() && childResults?.children?.length === 0) {
        childResults = await childSearch(baseSearchParams);
        status = 'fallback';
      }
      if (hasCriteria() && childResults?.children?.length === 0 || !hasCriteria()) {
        childResults = await getRandomChildren(DEFAULT_TAKE);
        if (hasCriteria()) {
          status = 'fallback';
        }
      }
      if (childResults?.children[0]) {
        try {
          details_0 = await getChild(childResults.children[0].globalId);
        } catch {
          console.error('Child missing details');
        }
      }
    } catch (error) {
      console.error('Error fetching children:', {
        error
      });
      status = 'fallback';
      try {
        childResults = await getRandomChildren(DEFAULT_TAKE);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', {
          fallbackError
        });
        status = 'error';
      }
    } finally {
      if (id === fetchIdRef.current) {
        setChildren([...(childResults?.children || [])]);
        setChildDetails(details_0);
        setResultsStatus(status);
      }
    }
  }, [gender, ages.ageMin, ages.ageMax, location, causes, hasCriteria]);
  const backwardsClick = useCallback(() => {
    if (step === LAST_STEP) {
      setCauses(null);
      setGender(null);
      setAges(null);
      setLocation(null);
      setSkipChild(0);
      setStep(0);
    } else {
      setStep(Math.max(step - 1, 0));
    }
  }, [step, setCauses, setGender, setAges, setLocation, setStep]);
  const forwardClick = useCallback(() => {
    if (step === LAST_STEP && children.length > 0) {
      setSkipChild(prev => (prev + 1) % children.length);
    } else if (step !== LAST_STEP) {
      setStep(Math.min(step + 1, LAST_STEP));
    }
  }, [step, children.length, setStep]);

  // Fetch children when arriving at the results step with changed filters
  useEffect(() => {
    if (step === LAST_STEP && filterKey !== lastFetchedKeyRef.current) {
      setChildren([]);
      setChildDetails(undefined);
      setSkipChild(0);
      fetchChildren();
      lastFetchedKeyRef.current = filterKey;
    }
  }, [step, filterKey, fetchChildren]);

  // Fetch details when cycling through children
  useEffect(() => {
    const fetchDetails = async () => {
      if (skipChild !== 0 && children.length > 0) {
        setResultsStatus('loading');
        const index = getDisplayIndex(skipChild, children.length);
        const newKid = children[index];
        await replaceDetails(newKid);
        setResultsStatus(prev_0 => prev_0 === 'loading' ? 'matched' : prev_0);
      }
    };
    fetchDetails();
  }, [skipChild, children]);
  const displayIndex = getDisplayIndex(skipChild, children.length);
  const currentChild = children[displayIndex];
  return {
    isSkipped,
    resultsStatus,
    children,
    childDetails,
    currentChild,
    backwardsClick,
    forwardClick,
    forwardDisabled: step === LAST_STEP && children.length === 0,
    isLastStep: step === LAST_STEP,
    isSecondToLastStep: step === SECOND_TO_LAST_STEP
  };
};